import Link from 'next/link';
import Image from 'next/image';
import { BlogPostMeta } from '@/lib/blog';
import { nameToSlug } from '@/lib/authors';

interface BlogCardProps {
  post: BlogPostMeta;
}

export default function BlogCard({ post }: BlogCardProps) {
  const authorSlug = nameToSlug(post.author);

  return (
    <article className="group overflow-hidden rounded-2xl border-2 border-gray-100 bg-white shadow-sm transition-all hover:border-[#FFC933] hover:shadow-xl">
      <Link href={`/blog/${post.slug}`}>
        <div className="relative aspect-video w-full overflow-hidden bg-gray-50">
          {post.image ? (
            <Image
              src={post.image}
              alt={post.title}
              fill
              className="object-contain transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#2B0A4A] to-[#1B0044]">
              <span className="text-4xl font-bold text-white opacity-20">VV</span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-6">
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {post.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#FFF8EC] px-3 py-1 text-xs font-medium text-[#2B0A4A]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <Link href={`/blog/${post.slug}`}>
          <h2 className="mb-3 text-2xl font-bold text-[#2B0A4A] transition-colors group-hover:text-[#FF8A3D]">
            {post.title}
          </h2>
        </Link>

        {/* Excerpt */}
        <Link href={`/blog/${post.slug}`}>
          <p className="mb-4 line-clamp-3 text-gray-600">
            {post.excerpt}
          </p>
        </Link>

        {/* Meta Info */}
        <div className="flex items-center justify-between border-t border-gray-100 pt-4 text-sm text-gray-500">
          <Link
            href={`/blog/author/${authorSlug}`}
            className="flex items-center gap-2 transition hover:text-[#2B0A4A]"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="font-medium hover:underline">{post.author}</span>
          </Link>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{post.readingTime}</span>
            </div>

            <div className="flex items-center gap-1">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
